import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SetupPanel } from './components/SetupPanel'
import { Stage } from './components/Stage'
import { Editor } from './components/Editor'
import { PublishPanel } from './components/PublishPanel'
import { RecordingEngine } from './lib/compositor'
import { FloatBar, floatBarSupported } from './lib/floatbar'
import { DEFAULT_CAM_LAYOUT, CamLayout, Clip, Source, uid } from './lib/types'
import { Icon } from './components/Icon'
import { SignIn } from './components/SignIn'
import { UserChip } from './components/UserChip'
import { SessionProvider, useSession } from './lib/session'
import {
  DeviceOption, describeMediaError, getCamera, getMic, getScreen, listDevices,
} from './lib/media'

type Phase = 'setup' | 'live' | 'edit' | 'publish'

export default function App() {
  return (
    <SessionProvider>
      <Gate />
    </SessionProvider>
  )
}

/**
 * Nothing about recording is mounted until we know who's here. That's
 * deliberate: the recorder asks for camera and microphone permission the moment
 * it loads, and asking a stranger for their webcam before they've even signed
 * in would be rude — and pointless, since they can't publish anything.
 */
function Gate() {
  const { me, loading } = useSession()
  if (loading) return <Splash />
  if (!me?.user) return <SignIn />
  return <Recorder />
}

function Splash() {
  return (
    <div className="signin">
      <div className="card signin-card">
        <span className="mark big"><Icon name="flower" size={22} /></span>
        <p className="muted" style={{ margin: 0 }}>Loading Bloom…</p>
      </div>
    </div>
  )
}

function Recorder() {
  const engineRef = useRef<RecordingEngine>()
  if (!engineRef.current) engineRef.current = new RecordingEngine()
  const engine = engineRef.current

  const [phase, setPhase] = useState<Phase>('setup')
  const [recState, setRecState] = useState<'idle' | 'recording' | 'paused'>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [micMuted, setMicMuted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [layout, setLayoutState] = useState<CamLayout>(DEFAULT_CAM_LAYOUT)
  const [sources, setSources] = useState<Source[]>([])
  const [clips, setClips] = useState<Clip[]>([])

  const [cameras, setCameras] = useState<DeviceOption[]>([])
  const [mics, setMics] = useState<DeviceOption[]>([])
  const [opts, setOpts] = useState({
    cameraId: '', micId: '', mirror: true, systemAudio: true, camOff: false, micOff: false,
  })

  const floatRef = useRef<FloatBar | null>(null)
  const [floating, setFloating] = useState(false)

  // Layout lives in two places on purpose: React state drives the UI, while the
  // engine reads a plain object every frame. Writing both keeps the 30fps draw
  // loop free of React's render cycle.
  const setLayout = useCallback((patch: Partial<CamLayout>) => {
    setLayoutState((prev) => {
      const next = { ...prev, ...patch }
      engine.layout = next
      return next
    })
  }, [engine])

  // ------------------------------------------------------------ device setup

  useEffect(() => {
    // Labels stay blank until permission is granted, so ask once up front.
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((s) => { s.getTracks().forEach((t) => t.stop()) })
      .catch(() => {})
      .finally(async () => {
        const { cameras, mics } = await listDevices()
        setCameras(cameras)
        setMics(mics)
        setOpts((o) => ({
          ...o,
          cameraId: o.cameraId || cameras[0]?.deviceId || '',
          micId: o.micId || mics[0]?.deviceId || '',
        }))
      })
  }, [])

  const micLevelRef = useRef(0)
  useEffect(() => { engine.onMicLevel = (l) => { micLevelRef.current = l } }, [engine])
  useEffect(() => { engine.layout = layout }, [engine, layout])
  useEffect(() => setLayout({ mirror: opts.mirror }), [opts.mirror, setLayout])
  useEffect(() => () => engine.dispose(), [engine])

  // Ticker for the timer + floating bar while a take is running.
  useEffect(() => {
    if (recState === 'idle') return
    const t = window.setInterval(() => setElapsed(engine.elapsed()), 200)
    return () => window.clearInterval(t)
  }, [recState, engine])

  useEffect(() => {
    floatRef.current?.update({
      recording: recState !== 'idle',
      paused: recState === 'paused',
      elapsed,
      micMuted,
      micLevel: micLevelRef.current,
      layout,
      aspect: engine.frameSize.width / Math.max(1, engine.frameSize.height),
    })
  }, [elapsed, recState, micMuted, layout, engine])

  // If the user hits Chrome's own "Stop sharing" button, wrap the take up cleanly.
  useEffect(() => {
    engine.onScreenEnded = () => {
      if (engine.state !== 'idle') void finishTake()
      else setPhase((p) => (p === 'live' ? (clips.length ? 'edit' : 'setup') : p))
    }
  })

  // ------------------------------------------------------------ take control

  const openCapture = async () => {
    setError(null)
    setBusy(true)
    try {
      const screen = await getScreen(opts.systemAudio)
      await engine.attachScreen(screen)
      await engine.attachCamera(opts.camOff ? null : await getCamera(opts.cameraId))
      await engine.attachMic(opts.micOff ? null : await getMic(opts.micId))
      setPhase('live')
      return true
    } catch (e) {
      setError(describeMediaError(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const startRecording = () => {
    engine.startTake()
    setRecState('recording')
    setElapsed(0)
    if (floatBarSupported() && !floatRef.current?.isOpen) void openFloatBar()
  }

  const finishTake = async () => {
    if (engine.state === 'idle') return
    const src = await engine.stopTake()
    setRecState('idle')
    setElapsed(0)
    floatRef.current?.close()
    if (src.duration < 0.4) { setPhase(clips.length ? 'edit' : 'live'); return }
    setSources((s) => [...s, src])
    setClips((c) => [...c, { id: uid(), sourceId: src.id, in: 0, out: src.duration }])
    setPhase('edit')
  }

  const pauseResume = () => {
    if (engine.state === 'recording') { engine.pause(); setRecState('paused') }
    else if (engine.state === 'paused') { engine.resume(); setRecState('recording') }
  }

  const toggleMic = () => {
    const next = !micMuted
    engine.setMicMuted(next)
    setMicMuted(next)
  }

  const recordMore = async () => {
    if (engine.hasScreen) { setPhase('live'); return }
    await openCapture() // the old share ended — ask for a new one
  }

  const startOver = () => {
    sources.forEach((s) => URL.revokeObjectURL(s.url))
    setSources([])
    setClips([])
    setPhase('setup')
  }

  // -------------------------------------------------------------- float bar

  const openFloatBar = async () => {
    if (!floatBarSupported()) {
      setError('Your browser has no floating-window support. Chrome 116+ or Edge is needed for that.')
      return
    }
    const bar = new FloatBar({
      onPause: () => { engine.pause(); setRecState('paused') },
      onResume: () => { engine.resume(); setRecState('recording') },
      onStop: () => void finishTake(),
      onToggleMic: toggleMic,
      onToggleCam: () => setLayout({ hidden: !engine.layout.hidden }),
      onMove: (x, y) => setLayout({ x, y }),
      onClosed: () => { floatRef.current = null; setFloating(false) },
    })
    try {
      await bar.open(engine.cameraStream, {
        recording: true, paused: false, elapsed: engine.elapsed(), micMuted,
        micLevel: micLevelRef.current, layout,
        aspect: engine.frameSize.width / Math.max(1, engine.frameSize.height),
      })
      floatRef.current = bar
      setFloating(true)
    } catch {
      setError('The floating window could not be opened.')
    }
  }

  const stepIndex = useMemo(
    () => ({ setup: 0, live: 0, edit: 1, publish: 2 }[phase]),
    [phase],
  )

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="mark"><Icon name="flower" size={15} /></span> Bloom
        </div>
        <div className="steps" role="status">
          {['Record', 'Edit', 'Publish'].map((s, i) => (
            <span key={s} className={i === stepIndex ? 'on' : ''}>{s}</span>
          ))}
        </div>
        <div className="spacer" />
        {sources.length > 0 && phase !== 'live' && (
          <button className="btn ghost small" onClick={startOver}>Start over</button>
        )}
        <UserChip />
      </header>

      <main className="main">
        {error && (
          <>
            <div className="notice error">{error}</div>
            <div style={{ height: 16 }} />
          </>
        )}

        {phase === 'setup' && (
          <SetupPanel
            cameras={cameras}
            mics={mics}
            {...opts}
            busy={busy}
            onChange={(patch) => setOpts((o) => ({ ...o, ...patch }))}
            onStart={openCapture}
          />
        )}

        {phase === 'live' && (
          <div className="card">
            <h2>{recState === 'idle' ? 'Ready when you are' : 'Recording'}</h2>
            <p className="sub">
              {recState === 'idle'
                ? 'This preview is exactly what will be recorded. Drag your face bubble anywhere, then hit record.'
                : 'Move the bubble any time — the change lands in the video immediately.'}
            </p>
            <Stage
              canvas={engine.canvas}
              layout={layout}
              live={recState !== 'idle'}
              onLayout={setLayout}
            />
            <div style={{ height: 16 }} />
            <div className="wrap-row">
              {recState === 'idle' ? (
                <>
                  <button className="btn danger big" onClick={startRecording}>
                    <Icon name="record" size={17} /> Start recording
                  </button>
                  {clips.length > 0 && (
                    <button className="btn" onClick={() => setPhase('edit')}>
                      <Icon name="arrowLeft" /> Back to editor
                    </button>
                  )}
                  <div style={{ flex: 1 }} />
                  <label className="toggle small">
                    <input
                      type="checkbox"
                      checked={!layout.hidden}
                      onChange={(e) => setLayout({ hidden: !e.target.checked })}
                    />
                    Show my face
                  </label>
                </>
              ) : floating ? (
                <span className="small muted">
                  Recording — use the floating window to pause or stop.
                </span>
              ) : (
                // Only a fallback: without the floating window there would be no
                // way to stop recording at all (Firefox and Safari have no
                // always-on-top window support).
                <>
                  <button className="btn" onClick={pauseResume}>
                    <Icon name={recState === 'paused' ? 'play' : 'pause'} />
                    {recState === 'paused' ? 'Resume' : 'Pause'}
                  </button>
                  <button className="btn danger" onClick={finishTake}>
                    <Icon name="stop" /> Stop
                  </button>
                  <div style={{ flex: 1 }} />
                  {floatBarSupported() && (
                    <button className="btn" onClick={openFloatBar}>
                      <Icon name="expand" /> Pop out controls
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {phase === 'edit' && (
          <Editor
            sources={sources}
            clips={clips}
            onClips={setClips}
            onRecordMore={recordMore}
            onPublish={() => setPhase('publish')}
          />
        )}

        {phase === 'publish' && (
          <PublishPanel
            sources={sources}
            clips={clips}
            onBack={() => setPhase('edit')}
            onStartOver={startOver}
          />
        )}
      </main>

    </div>
  )
}
